import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { adminFetch } from '../lib/adminFetch';
import { API_BASE } from '../lib/apiBase';

const AdminAuthContext = createContext(null);

// Access token lives 15 min on the backend
const ACCESS_TTL_MS = 15 * 60 * 1000;
// Auto-refresh when fewer than this many ms remain
const PROACTIVE_REFRESH_THRESHOLD_MS = 60 * 1000;
const EXPIRES_AT_KEY = 'admin_access_expires_at';

function setExpiresAt(ts) {
  try { localStorage.setItem(EXPIRES_AT_KEY, String(ts)); } catch {}
}
function getExpiresAt() {
  try {
    const v = localStorage.getItem(EXPIRES_AT_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch { return 0; }
}
function clearExpiresAt() {
  try { localStorage.removeItem(EXPIRES_AT_KEY); } catch {}
}

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msRemaining, setMsRemaining] = useState(0);

  const BACKEND_URL = API_BASE;
  const refreshingRef = useRef(false);

  const checkAuth = React.useCallback(async () => {
    // Don't hit /api/admin/me on public pages — only when user is inside /admin/*
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
      setAdmin(false);
      setLoading(false);
      return;
    }
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAdmin(data);
        // If we don't have an expiry yet, assume freshly issued
        if (!getExpiresAt()) setExpiresAt(Date.now() + ACCESS_TTL_MS);
      } else {
        setAdmin(false);
        clearExpiresAt();
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setAdmin(false);
      clearExpiresAt();
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Login failed');
    }
    const data = await response.json();
    setAdmin(data);
    setExpiresAt(Date.now() + ACCESS_TTL_MS);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAdmin(false);
      clearExpiresAt();
    }
  };

  const refreshSession = React.useCallback(async () => {
    if (refreshingRef.current) return false;
    refreshingRef.current = true;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (r.ok) {
        setExpiresAt(Date.now() + ACCESS_TTL_MS);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshingRef.current = false;
    }
  }, [BACKEND_URL]);

  // Tick countdown + proactive auto-refresh
  useEffect(() => {
    if (!admin) {
      setMsRemaining(0);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      const expiresAt = getExpiresAt();
      const remaining = Math.max(0, expiresAt - Date.now());
      setMsRemaining(remaining);
      if (remaining > 0 && remaining < PROACTIVE_REFRESH_THRESHOLD_MS && !refreshingRef.current) {
        const ok = await refreshSession();
        if (!cancelled && ok) {
          setMsRemaining(ACCESS_TTL_MS);
        }
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [admin, refreshSession]);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout, checkAuth, refreshSession, msRemaining }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
